/**
 * Search Provider Admin Dashboard
 * 
 * Admin interface for managing the search provider switching system:
 * - Real-time usage statistics and cost savings with POI/Geocoding indicators
 * - Provider configuration and priority management
 * - API call monitoring and analytics
 * - System alerts and health monitoring
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  TextField,
  Button,
  Divider
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  MoneyOff as MoneyOffIcon
} from '@mui/icons-material';

export default function SearchProviderAdmin() {
  const [usage, setUsage] = useState<any[]>([]);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string>('');

  useEffect(() => {
    loadUsage();
    loadAlerts();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadUsage();
      loadAlerts();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const loadUsage = async () => {
    try {
      const response = await fetch('/api/search-providers/admin/usage', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setUsage(data.usage);
        setLastUpdated(data.lastUpdated);
      }
    } catch (error) {
      console.error('Error loading search usage:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAlerts = async () => {
    try {
      const response = await fetch('/api/search-providers/admin/alerts', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        setAlerts(data.alerts);
      }
    } catch (error) {
      console.error('Error loading alerts:', error);
    }
  };

  const updateProviderLimits = async (provider: any) => {
    try {
      // Only send the fields that can be updated via the limits endpoint
      const updateData = {
        provider: provider.provider,
        daily_limit: provider.daily_limit,
        monthly_limit: provider.monthly_limit,
        auto_switch_threshold: provider.auto_switch_threshold,
        priority_order: provider.priority_order,
        enabled: provider.enabled
      };

      const response = await fetch('/api/search-providers/admin/limits', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(updateData)
      });
      
      const data = await response.json();
      if (data.success) {
        loadUsage(); // Refresh usage data to show updated values
        alert(`${provider.display_name || provider.provider} settings updated successfully!`);
      } else {
        alert(`Error updating provider: ${data.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error updating provider limits:', error);
      alert('Error updating provider limits');
    }
  };

  const toggleProviderEnabled = async (providerName: string, enabled: boolean) => {
    try {
      // Optimistically update the UI
      setUsage(prevUsage => 
        prevUsage.map(provider => 
          provider.provider === providerName 
            ? { ...provider, enabled } 
            : provider
        )
      );

      // Update the backend - only send provider name and enabled status
      const response = await fetch('/api/search-providers/admin/limits', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          provider: providerName,
          enabled: enabled
        })
      });
      
      const data = await response.json();
      if (!data.success) {
        // Revert the optimistic update if it failed
        setUsage(prevUsage => 
          prevUsage.map(provider => 
            provider.provider === providerName 
              ? { ...provider, enabled: !enabled } 
              : provider
          )
        );
        alert(`Error updating provider status: ${data.error || 'Unknown error'}`);
      } else {
        // Show success message
        console.log(`Provider ${providerName} ${enabled ? 'enabled' : 'disabled'} successfully`);
      }
    } catch (error) {
      console.error('Error toggling provider enabled status:', error);
      // Revert the optimistic update
      setUsage(prevUsage => 
        prevUsage.map(provider => 
          provider.provider === providerName 
            ? { ...provider, enabled: !enabled } 
            : provider
        )
      );
      alert('Error updating provider status');
    }
  };

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    return 'primary';
  };

  const getStatusChip = (provider: any) => {
    if (!provider.enabled) {
      return <Chip label="Disabled" color="default" size="small" />;
    }
    if (provider.should_auto_switch) {
      return <Chip label="Limit Reached" color="error" size="small" icon={<ErrorIcon />} />;
    }
    if (Math.max(Number(provider.daily_usage_percent) || 0, Number(provider.monthly_usage_percent) || 0) >= 75) {
      return <Chip label="High Usage" color="warning" size="small" icon={<WarningIcon />} />;
    }
    return <Chip label="Available" color="success" size="small" icon={<CheckCircleIcon />} />;
  };

  const getProviderTypeChips = (provider: any) => {
    const chips = [];
    
    if (provider.supports_business_search) {
      chips.push(
        <Chip 
          key="poi"
          label="POI Search" 
          color="primary" 
          size="small" 
          icon={<BusinessIcon />}
          sx={{ mr: 0.5 }}
        />
      );
    }
    
    if (provider.supports_geocoding) {
      chips.push(
        <Chip 
          key="geocoding"
          label="Geocoding" 
          color="secondary" 
          size="small" 
          icon={<LocationIcon />}
          sx={{ mr: 0.5 }}
        />
      );
    }
    
    return chips;
  };

  const getCostChip = (provider: any) => {
    if (provider.is_free_tier) {
      return <Chip label="Free" color="success" size="small" icon={<MoneyOffIcon />} />;
    } else if (provider.cost_per_1000) {
      return <Chip label={`$${provider.cost_per_1000}/1K`} color="warning" size="small" icon={<MoneyIcon />} />;
    } else {
      return <Chip label="Paid" color="warning" size="small" icon={<MoneyIcon />} />;
    }
  };

  // Group providers by their supported capabilities
  const poiProviders = usage.filter(p => p.supports_business_search);
  const geocodingProviders = usage.filter(p => p.supports_geocoding);
  const hybridProviders = usage.filter(p => p.supports_business_search && p.supports_geocoding);

  if (loading) {
    return <div>Loading search provider settings...</div>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Usage Alerts */}
      {alerts.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            ⚠️ Usage Alerts
          </Typography>
          {alerts.map((alert, index) => (
            <Typography key={index} variant="body2">
              • <strong>{alert.provider}</strong>: {Math.max(Number(alert.daily_usage_percent) || 0, Number(alert.monthly_usage_percent) || 0).toFixed(1)}% used 
              {alert.daily_remaining !== null && alert.daily_remaining < 100 && ` (${alert.daily_remaining} daily remaining)`}
              {alert.monthly_remaining !== null && alert.monthly_remaining < 500 && ` (${alert.monthly_remaining} monthly remaining)`}
            </Typography>
          ))}
        </Alert>
      )}

      {/* Two-Step Cost Optimization Summary */}
      <Card sx={{ backgroundColor: 'primary.50' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            💡 Two-Step Cost Optimization Active
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Step 1:</strong> Use POI search APIs (Yelp, HERE, Foursquare) to find business name + address<br/>
            <strong>Step 2:</strong> Use FREE geocoding APIs (Nominatim, LocationIQ) to convert address to coordinates<br/>
            <strong>Result:</strong> 90-100% cost savings vs traditional full-service geocoding APIs
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Chip label={`${poiProviders.length} POI Providers`} color="primary" size="small" icon={<BusinessIcon />} />
            <Chip label={`${geocodingProviders.length} Geocoding Providers`} color="secondary" size="small" icon={<LocationIcon />} />
            <Chip label={`${hybridProviders.length} Hybrid Providers`} color="info" size="small" />
          </Box>
        </CardContent>
      </Card>

      {/* Real-time Usage Dashboard */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              📊 Provider Usage Dashboard
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Last updated: {new Date(lastUpdated).toLocaleTimeString()}
              </Typography>
              <IconButton size="small" onClick={loadUsage}>
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>
          
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Provider</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Cost</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Daily Usage</TableCell>
                  <TableCell>Monthly Usage</TableCell>
                  <TableCell>Priority</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {usage.map((provider) => (
                  <TableRow 
                    key={provider.provider}
                    sx={{ 
                      backgroundColor: provider.should_auto_switch ? 'rgba(244, 67, 54, 0.1)' : 'inherit'
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {provider.display_name || provider.provider}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {provider.provider}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {getProviderTypeChips(provider)}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {getCostChip(provider)}
                    </TableCell>
                    <TableCell>
                      {getStatusChip(provider)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: '100%', mr: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(Number(provider.daily_usage_percent) || 0, 100)}
                              color={getUsageColor(Number(provider.daily_usage_percent) || 0)}
                            />
                          </Box>
                          <Box sx={{ minWidth: 35 }}>
                            <Typography variant="body2" color="text.secondary">
                              {(Number(provider.daily_usage_percent) || 0).toFixed(1)}%
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {provider.daily_requests} / {provider.daily_limit || '∞'}
                          {provider.daily_remaining !== null && ` (${provider.daily_remaining} left)`}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ width: '100%', mr: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(Number(provider.monthly_usage_percent) || 0, 100)}
                              color={getUsageColor(Number(provider.monthly_usage_percent) || 0)}
                            />
                          </Box>
                          <Box sx={{ minWidth: 35 }}>
                            <Typography variant="body2" color="text.secondary">
                              {(Number(provider.monthly_usage_percent) || 0).toFixed(1)}%
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {provider.monthly_requests} / {provider.monthly_limit || '∞'}
                          {provider.monthly_remaining !== null && ` (${provider.monthly_remaining} left)`}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      #{provider.priority_order}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Provider Categories */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">🔍 Step 1: POI/Business Search Providers</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These providers search for businesses and return business name + address data.
            Used in Step 1 of the two-step cost optimization process.
          </Typography>
          
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Provider</TableCell>
                  <TableCell>Cost</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Monthly Limit</TableCell>
                  <TableCell>Usage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {poiProviders.map((provider) => (
                  <TableRow key={provider.provider}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BusinessIcon color="primary" />
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {provider.display_name || provider.provider}
                          </Typography>
                          {provider.supports_geocoding && (
                            <Chip label="Also does geocoding" size="small" color="secondary" />
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{getCostChip(provider)}</TableCell>
                    <TableCell>{getStatusChip(provider)}</TableCell>
                    <TableCell>
                      {provider.monthly_limit || 'Unlimited'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {provider.monthly_requests} / {provider.monthly_limit || '∞'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({(Number(provider.monthly_usage_percent) || 0).toFixed(1)}%)
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">🌍 Step 2: FREE Geocoding Providers</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These providers convert addresses to coordinates for FREE.
            Used in Step 2 to get precise latitude/longitude from business addresses.
          </Typography>
          
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Provider</TableCell>
                  <TableCell>Cost</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Daily Limit</TableCell>
                  <TableCell>Usage</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {geocodingProviders.filter(p => !p.supports_business_search).map((provider) => (
                  <TableRow key={provider.provider}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocationIcon color="secondary" />
                        <Typography variant="body2" fontWeight="bold">
                          {provider.display_name || provider.provider}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{getCostChip(provider)}</TableCell>
                    <TableCell>{getStatusChip(provider)}</TableCell>
                    <TableCell>
                      {provider.daily_limit || 'Unlimited'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {provider.daily_requests} / {provider.daily_limit || '∞'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ({(Number(provider.daily_usage_percent) || 0).toFixed(1)}%)
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      {/* Provider Configuration */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">⚙️ Provider Configuration</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure usage limits and auto-switching thresholds for each provider. 
            When usage exceeds the threshold, the system will automatically switch to the next available provider.
          </Typography>
          
          {usage.map((provider) => (
            <Card key={provider.provider} sx={{ mb: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 200 }}>
                      <Typography variant="h6" color="primary">
                        {provider.display_name || provider.provider}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                        {getProviderTypeChips(provider)}
                        {getCostChip(provider)}
                      </Box>
                      {getStatusChip(provider)}
                    </Box>
                    
                    <TextField
                      label="Daily Limit"
                      type="number"
                      size="small"
                      defaultValue={provider.daily_limit || 0}
                      helperText="0 = unlimited"
                      sx={{ minWidth: 120 }}
                      onBlur={(e) => {
                        provider.daily_limit = parseInt(e.target.value) || 0;
                      }}
                    />
                    
                    <TextField
                      label="Monthly Limit"
                      type="number"
                      size="small"
                      defaultValue={provider.monthly_limit || 0}
                      helperText="0 = unlimited"
                      sx={{ minWidth: 120 }}
                      onBlur={(e) => {
                        provider.monthly_limit = parseInt(e.target.value) || 0;
                      }}
                    />
                    
                    <TextField
                      label="Switch Threshold"
                      type="number"
                      size="small"
                      inputProps={{ min: 0.1, max: 1.0, step: 0.1 }}
                      defaultValue={provider.auto_switch_threshold || 0.8}
                      helperText="0.8 = 80%"
                      sx={{ minWidth: 120 }}
                      onBlur={(e) => {
                        provider.auto_switch_threshold = parseFloat(e.target.value) || 0.8;
                      }}
                    />
                    
                    <TextField
                      label="Priority"
                      type="number"
                      size="small"
                      defaultValue={provider.priority_order || 999}
                      helperText="Lower = higher priority"
                      sx={{ minWidth: 80 }}
                      onBlur={(e) => {
                        provider.priority_order = parseInt(e.target.value) || 999;
                      }}
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={provider.enabled}
                          onChange={(e) => {
                            toggleProviderEnabled(provider.provider, e.target.checked);
                          }}
                        />
                      }
                      label="Enabled"
                    />
                    
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => updateProviderLimits(provider)}
                    >
                      Update
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}